import type { Header, SortDirection } from '@tanstack/react-table';
import { flexRender } from '@tanstack/react-table';
import {
  type AriaAttributes,
  type ForwardedRef,
  forwardRef,
  type HTMLProps,
  type KeyboardEventHandler,
  useId,
  useMemo,
} from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '~/lib/cn';
import { variants } from '~/lib/variants';

import { useTableContext } from '../../hooks/useTableContext';
import { SortIcon } from './SortIcon';

interface TableHeadCellProps<TData, TValue>
  extends Omit<HTMLProps<HTMLTableCellElement>, 'className'> {
  header: Header<TData, TValue>;
  sortDirection?: SortDirection;
}

const tableHeadCellVariants = variants({
  base: cn(
    'p-md',
    'text-body-sm',
    'font-normal',
    'border-none',
    'transition-all',
    'duration-300',
  ),
  variants: {
    isSortable: {
      true: cn('hover:text-strong', 'cursor-pointer'),
    },
    size: {
      xs: cn('p-xs'),
      sm: cn('px-sm py-xs'),
      md: cn('px-md py-sm'),
      lg: cn('px-lg py-md'),
      xl: cn('px-xl py-lg'),
    },
    align: {
      left: '',
      right: cn('text-right'),
    },
  },
});

const tableHeadCellInnerVariants = variants({
  base: cn('flex w-full min-w-0 items-center gap-xs'),
  variants: {
    align: {
      left: cn('justify-start'),
      right: cn('justify-end text-right'),
    },
  },
});

function TableHeadCellInner<TData, TValue>(
  { header, ...props }: TableHeadCellProps<TData, TValue>,
  ref: ForwardedRef<HTMLTableCellElement>,
) {
  const { size } = useTableContext();
  const { t } = useTranslation('chords', { keyPrefix: 'table.columnSort' });
  const headerLabelId = useId();
  const sortStateDescriptionId = useId();

  const isSortable = header.column.getCanSort();
  const sortDirection = header.column.getIsSorted();
  const align = header.column.columnDef.meta?.align ?? 'left';

  const sortStateDescription = useMemo(() => {
    if (!isSortable) {
      return '';
    }

    if (sortDirection === 'asc') {
      return t('asc');
    }

    if (sortDirection === 'desc') {
      return t('desc');
    }

    return t('none');
  }, [isSortable, sortDirection, t]);

  const toggleSort = (e: unknown) => {
    if (isSortable) {
      header.column.getToggleSortingHandler()?.(e);
    }
  };

  const onKeyDown: KeyboardEventHandler = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      toggleSort(e);
    }
  };

  let sortLabel: AriaAttributes['aria-sort'] = 'none';

  if (sortDirection === 'asc') {
    sortLabel = 'ascending';
  } else if (sortDirection === 'desc') {
    sortLabel = 'descending';
  }

  return (
    <th
      {...props}
      ref={ref}
      aria-describedby={isSortable ? sortStateDescriptionId : undefined}
      aria-labelledby={isSortable ? headerLabelId : undefined}
      className={tableHeadCellVariants({
        isSortable,
        size,
        align: align === 'right' ? 'right' : 'left',
      })}
      style={{
        width: `calc(var(--header-${header.id}-size) * 1px)`,
      }}
      tabIndex={isSortable ? 0 : undefined}
      aria-sort={sortLabel}
      onClick={toggleSort}
      onKeyDown={onKeyDown}
    >
      <div
        className={tableHeadCellInnerVariants({
          align: align === 'right' ? 'right' : 'left',
        })}
      >
        {isSortable ? (
          <span className="min-w-0" id={headerLabelId}>
            {flexRender(header.column.columnDef.header, header.getContext())}
          </span>
        ) : (
          flexRender(header.column.columnDef.header, header.getContext())
        )}
        {isSortable ? (
          <>
            <SortIcon direction={sortDirection} />
            <span className="sr-only" id={sortStateDescriptionId}>
              {sortStateDescription}
            </span>
          </>
        ) : null}
      </div>
    </th>
  );
}

TableHeadCellInner.displayName = 'TableHeadCell';

export const TableHeadCell = forwardRef(TableHeadCellInner) as <TData, TValue>(
  props: TableHeadCellProps<TData, TValue> & {
    ref?: React.ForwardedRef<HTMLTableCellElement>;
  },
) => ReturnType<typeof TableHeadCellInner>;
